import axios from "axios";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import { fullAPIURL } from "../utils/requests";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";
import { Nullable } from "../types/nullable";

const metricsAxios = axios.create({
  baseURL: `${fullAPIURL}/v2/metrics`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

metricsAxios.interceptors.request.use(requestAccessTokenInterceptor);

metricsAxios.interceptors.request.use(refreshAccessTokenInterceptor);

metricsAxios.interceptors.response.use(responseStatusInterceptor);

export type UserInfo = {
  clientUuid: Nullable<string>;
  firstName: string;
  lastName: string;
  phone: Nullable<string>;
  userUuid: Nullable<string>;
  email: Nullable<string>;
  userImageUrl: Nullable<string>;
  userImageKey: Nullable<string>;
};

export type OverduePayments = UserInfo & {
  amount: number;
  planName: string;
};

export type OpenInvoices = UserInfo & {
  invoiceUuid: string;
  total: Nullable<number>;
};

export type AtRiskMembers = UserInfo & {
  lastCheckin: number;
  isLead: boolean;
  isMember: boolean;
  assignedToFirstName: Nullable<string>;
  assignedToLastName: Nullable<string>;
};

export type ExpiringMembers = UserInfo & {
  amount: number;
  discountAmount: number;
  planName: string;
};

export type NewLeads = UserInfo & {
  leadCreatedTimestamp: Nullable<number>;
  leadConvertTimestamp: Nullable<number>;
  referredById: Nullable<number>;
  referralSource: Nullable<string>;
  referralSourceValue: Nullable<string>;
};

export type SubscriptionStatus =
  | "active"
  | "alert"
  | "cancelled"
  | "completed"
  | "paused"
  | "pendingactivation"
  | "pendcancel";

export type NewMemberships = UserInfo & {
  subscriptionId: number;
  subscriptionUuid: string;
  amount: number;
  discountAmount: number;
  startDate: Nullable<Date>;
  cancelDate: Nullable<Date>;
  status: Nullable<SubscriptionStatus>;
  planUuid: string;
  planName: string;
};

export type CelebrationType = "anniversary" | "birthday";
export type CelebrationsProfile = UserInfo & {
  celebrationDate: Date;
  type: CelebrationType;
};

export type CelebrationsMetadata = {
  userUuid: string;
  eventType: CelebrationType;
};

export type SubscriptionCancelRequest = {
  uuid: string;
  userUuid: string;
  clientUuid: string;
  subscriptionUuid: string;
  status: "pendrequest" | "subchanged";
  comment: Nullable<string>;
  createdAt: string;
  updatedAt: string;
  planName: string;
  firstName: string;
  lastName: string;
  nextChargeDate: string;
  userImageUrl: Nullable<string>;
};

export type DashboardSummaryData = {
  overdueInvoices: Nullable<OverduePayments[]>;
  openInvoices: Nullable<OpenInvoices[]>;
  atRiskMembers: Nullable<AtRiskMembers[]>;
  expiringMembers: Nullable<ExpiringMembers[]>;
  newLeads: Nullable<NewLeads[]>;
  newMemberships: Nullable<NewMemberships[]>;
  celebrations: Nullable<CelebrationsProfile[]>;
  celebrationsMetadata: Nullable<CelebrationsMetadata[]>;
  cancelRequests: Nullable<SubscriptionCancelRequest[]>;
};

export type MetricComparison = {
  metric: string;
  current: number;
  previous: number;
};

export type MyGymMetricsResponse = {
  data: MetricComparison[];
};

export async function getDashboardSummaryData(): Promise<DashboardSummaryData> {
  const response = await metricsAxios.get("/dashboard-summary");
  return response.data;
}

export async function getMyGymMetrics(params: {
  start: string;
  end: string;
}): Promise<MyGymMetricsResponse> {
  const response = await metricsAxios.get("/my-gym-metrics", { params });
  return response.data;
}
